import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import CardNB from '../../components/ui-nb/CardNB'
import ButtonNB from '../../components/ui-nb/ButtonNB'
import IconNB from '../../components/ui-nb/IconNB'
import { supabase } from '../../lib/supabase'

interface OrderSummary {
  release_code: string
  device: string
  status: string
  customer_name: string
  device_brand?: string
  device_model?: string
}

type VerifyState = 'idle' | 'loading' | 'success' | 'error' | 'locked' | 'not_found'

export default function VerifyDelivery() {
  const { order_id } = useParams<{ lang: string; order_id: string }>()
  const [order, setOrder] = useState<OrderSummary | null>(null)
  const [otpCode, setOtpCode] = useState('')
  const [verifyState, setVerifyState] = useState<VerifyState>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [isLoadingOrder, setIsLoadingOrder] = useState(true)
  const [attemptsRemaining, setAttemptsRemaining] = useState(5)

  useEffect(() => {
    if (!order_id) return

    async function fetchOrder() {
      setIsLoadingOrder(true)
      try {
        const { data, error } = await supabase
          .rpc('get_public_order_by_id', { p_order_id: order_id })
          .single()

        if (error || !data) {
          setVerifyState('not_found')
          setIsLoadingOrder(false)
          return
        }

        setOrder(data as OrderSummary)
        setVerifyState('idle')
      } catch {
        setVerifyState('error')
        setErrorMessage('Failed to load order information.')
      }
      setIsLoadingOrder(false)
    }

    fetchOrder()
  }, [order_id])

  const handleOtpChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 6)
    setOtpCode(cleaned)
    if (verifyState === 'error' || verifyState === 'locked') {
      setVerifyState('idle')
      setErrorMessage('')
    }
  }

  const handleVerify = async () => {
    if (!order_id || otpCode.length !== 6) return

    setVerifyState('loading')
    setErrorMessage('')

    try {
      const { data, error } = await supabase.functions.invoke('verify-otp', {
        body: { order_id, otp_code: otpCode }
      })

      if (error) {
        setVerifyState('error')
        setErrorMessage('Verification failed. Please check your connection and try again.')
        return
      }

      if (data.locked) {
        setVerifyState('locked')
        setErrorMessage('Account locked. Too many failed attempts. Please contact support.')
        setAttemptsRemaining(0)
        return
      }

      if (data.error) {
        const attemptsLeft = Math.max(0, attemptsRemaining - 1)
        setAttemptsRemaining(attemptsLeft)

        if (attemptsLeft === 0) {
          setVerifyState('locked')
          setErrorMessage('Account locked. Too many failed attempts. Please contact support.')
        } else {
          setVerifyState('error')
          setErrorMessage(`${data.error} (${attemptsLeft} attempts remaining)`)
        }
        return
      }

      if (data.success) {
        setVerifyState('success')
      }
    } catch {
      setVerifyState('error')
      setErrorMessage('Verification failed. Please check your connection and try again.')
    }
  }

  if (isLoadingOrder) {
    return (
      <div className="min-h-screen bg-nb-cream flex items-center justify-center">
        <div className="text-nb-text-muted">Loading...</div>
      </div>
    )
  }

  if (verifyState === 'not_found' || !order) {
    return (
      <div className="min-h-screen bg-nb-cream flex items-center justify-center p-4">
        <CardNB className="p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-nb-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <IconNB name="alert" size={32} className="text-nb-red" />
          </div>
          <h1 className="text-xl font-bold text-black mb-2">Order not found</h1>
          <p className="text-nb-text-muted mb-4">
            This verification link may be invalid or expired.
          </p>
          <p className="text-sm text-nb-text-muted">
            Please contact support if you believe this is an error.
          </p>
        </CardNB>
      </div>
    )
  }

  if (verifyState === 'success') {
    return (
      <div className="min-h-screen bg-nb-cream flex items-center justify-center p-4">
        <CardNB className="p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <IconNB name="check" size={32} className="text-green-600" />
          </div>
          <h1 className="text-xl font-bold text-black mb-2">Delivery Verified!</h1>
          <p className="text-nb-text-muted mb-6">
            Your device is ready. The delivery has been confirmed.
          </p>

          <div className="bg-nb-cream rounded-lg p-4 text-left mb-6">
            <h2 className="text-sm font-semibold text-nb-text-muted mb-3">Delivery Details</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-nb-text-muted">Folio</span>
                <span className="font-medium text-black">{order.release_code}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-nb-text-muted">Device</span>
                <span className="font-medium text-black">{order.device}</span>
              </div>
              {order.device_brand && (
                <div className="flex justify-between">
                  <span className="text-nb-text-muted">Brand</span>
                  <span className="font-medium text-black">{order.device_brand}</span>
                </div>
              )}
              {order.device_model && (
                <div className="flex justify-between">
                  <span className="text-nb-text-muted">Model</span>
                  <span className="font-medium text-black">{order.device_model}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-nb-text-muted">Client</span>
                <span className="font-medium text-black">{order.customer_name}</span>
              </div>
            </div>
          </div>

          <p className="text-sm text-nb-text-muted">
            Thank you for choosing TellowUp
          </p>
        </CardNB>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-nb-cream flex items-center justify-center p-4">
      <CardNB className="p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <h1 className="text-xl font-bold text-black mb-2">Verify Delivery</h1>
          <p className="text-nb-text-muted text-sm">
            Enter the 6-digit code sent to your phone or email
          </p>
        </div>

        {/* Order summary */}
        <div className="bg-nb-cream rounded-lg p-4 mb-6">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-nb-text-muted">Folio</span>
              <span className="font-medium text-black">{order.release_code}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-nb-text-muted">Device</span>
              <span className="font-medium text-black">{order.device}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-nb-text-muted">Client</span>
              <span className="font-medium text-black">{order.customer_name}</span>
            </div>
          </div>
        </div>

        {/* OTP Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-black mb-2">
            Verification Code
          </label>
          <input
            type="text"
            value={otpCode}
            onChange={(e) => handleOtpChange(e.target.value)}
            placeholder="000000"
            maxLength={6}
            className="w-full px-4 py-3 text-center text-2xl tracking-widest border border-nb-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-nb-yellow focus:border-transparent font-mono"
            disabled={verifyState === 'locked'}
          />
          {attemptsRemaining < 5 && verifyState !== 'locked' && (
            <p className="text-xs text-nb-text-muted mt-2 text-center">
              {attemptsRemaining} attempts remaining
            </p>
          )}
        </div>

        {/* Error message */}
        {(verifyState === 'error' || verifyState === 'locked') && errorMessage && (
          <div className="mb-4 p-3 bg-nb-red/10 border border-nb-red/20 rounded-lg">
            <p className="text-sm text-nb-red text-center">{errorMessage}</p>
          </div>
        )}

        {/* Verify button */}
        <ButtonNB
          variant="primary"
          size="lg"
          onClick={handleVerify}
          loading={verifyState === 'loading'}
          disabled={otpCode.length !== 6 || verifyState === 'locked'}
          className="w-full"
        >
          {verifyState === 'loading' ? 'Verifying...' : 'Verify'}
        </ButtonNB>

        <p className="text-xs text-nb-text-muted mt-4 text-center">
          Didn't receive a code? Contact support for assistance.
        </p>
      </CardNB>
    </div>
  )
}